import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useChat } from "../contexts/ChatContext";
import { useProposal } from "../contexts/ProposalContext";
import { useEditableDocument } from "../contexts/EditableDocumentContext";
import { useStreamingChat } from "../hooks/useStreamingChat";
import apiClient from "../services/apiClient";

// Components
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";

// Chat Components
import ChatSidebar from "../components/chat/ChatSidebar";
import MessageBubble from "../components/chat/MessageBubble";
import MessageInput from "../components/chat/MessageInput";
import DocumentSidebar from "../components/chat/DocumentSidebar";
import PredefinedPromptsDropdown from "../components/chat/PredefinedPromptsDropdown";

// Document Components
import DocumentEditor from "../components/documents/DocumentEditor";
import CreateDocumentModal from "../components/documents/CreateDocumentModal";
import HolisticGenerationModal from "../components/documents/HolisticGenerationModal";
import DuplicateDocumentModal from "../components/documents/DuplicateDocumentModal";

// Upload Modal
import DocumentUploadModal from "../components/documents/DocumentUploadModal";

// Common Components
import Modal from "../components/common/Modal";

const ProposalWorkspace = () => {
  const { proposalId, documentId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // Contexts
  const {
    currentSession,
    messages,
    setMessages, // FIX: Get setMessages from ChatContext
    loading: chatLoading,
    error: chatError,
    isTyping,
    fetchSession,
    sendMessage,
    createSession,
    clearCurrentSession, // This is the key function we need
    getProposalSessions,
    refreshSessions,
  } = useChat();

  const {
    currentProposal,
    fetchProposal,
    loading: proposalLoading,
  } = useProposal();

  const {
    documents,
    currentDocument,
    templates,
    loading: documentsLoading,
    error: documentsError,
    fetchDocuments,
    fetchDocument,
    clearCurrentDocument,
    deleteDocument,
  } = useEditableDocument();

  // Local state
  const [activeTab, setActiveTab] = useState(documentId ? "create" : "chat");
  const [chatSidebarOpen, setChatSidebarOpen] = useState(true);
  const [documentSidebarOpen, setDocumentSidebarOpen] = useState(true);
  const [referencedDocuments, setReferencedDocuments] = useState([]);
  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [showHolisticModal, setShowHolisticModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [documentRefreshCount, setDocumentRefreshCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [documentToDuplicate, setDocumentToDuplicate] = useState(null);

  // Model selection state
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(true);

  // Streaming chat hook
  const {
    isStreaming,
    streamingContent,
    sendStreamingMessage,
    completeStreaming,
    clearStreamingContent,
    getFinalStreamedContent,
  } = useStreamingChat();

  // CRITICAL FIX: Clear any general sessions when entering proposal workspace
  useEffect(() => {
    console.log(
      "ProposalWorkspace: Component mounted - clearing any general sessions"
    );

    // Clear current session and messages immediately when entering proposal workspace
    // This ensures no general chat content bleeds into proposal workspace
    clearCurrentSession();

    // Also dispatch the event to clear any proposal sessions from other contexts
    window.dispatchEvent(new Event("clearProposalSession"));
  }, []); // Empty dependency array means this runs once on mount

  // Load proposal data
  useEffect(() => {
    if (proposalId) {
      fetchProposal(parseInt(proposalId));
      fetchDocuments(parseInt(proposalId));
    }
  }, [proposalId, fetchProposal, fetchDocuments]);

  // Load specific document if documentId is provided
  useEffect(() => {
    if (documentId && !isNaN(parseInt(documentId))) {
      fetchDocument(parseInt(documentId));
      setActiveTab("create");
    } else if (!documentId) {
      clearCurrentDocument();
    }
  }, [documentId, fetchDocument, clearCurrentDocument]);

  // Load or create chat session for this proposal - UPDATED with better session management
  useEffect(() => {
    if (proposalId && !currentChatSession) {
      console.log(
        "ProposalWorkspace: Looking for existing proposal sessions..."
      );
      const proposalSessions = getProposalSessions(parseInt(proposalId));
      console.log(
        "ProposalWorkspace: Found proposal sessions:",
        proposalSessions.length
      );

      if (proposalSessions.length > 0) {
        // Use the most recent session
        const mostRecent = proposalSessions[0];
        console.log(
          "ProposalWorkspace: Using most recent session:",
          mostRecent.session_id
        );
        setCurrentChatSession(mostRecent);
        fetchSession(mostRecent.session_id);
      } else {
        // Create a new session for this proposal
        console.log(
          "ProposalWorkspace: No existing sessions, creating new one"
        );
        createProposalSession();
      }
    }
  }, [proposalId, currentChatSession, getProposalSessions, fetchSession]);

  // Load available AI models
  useEffect(() => {
    loadAvailableModels();
  }, []);

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();
      const models = response.data?.models || response.models || [];
      setAvailableModels(models);

      // Set default model
      const defaultModel = models.find((m) => m.isDefault);
      if (defaultModel) {
        setSelectedModel(defaultModel.model);
      }
    } catch (error) {
      console.error("ProposalWorkspace: Failed to load available models:", error);
    } finally {
      setModelsLoading(false);
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (activeTab === "chat") {
      scrollToBottom();
    }
  }, [messages, isTyping, streamingContent, activeTab]); // FIX: Added streamingContent dependency

  const createProposalSession = async () => {
    if (currentProposal) {
      try {
        console.log(
          "ProposalWorkspace: Creating new proposal session for:",
          currentProposal.proposal_name
        );
        const sessionName = `${currentProposal.proposal_name} Discussion`;
        const newSession = await createSession(
          sessionName,
          parseInt(proposalId)
        );
        if (newSession) {
          const sessionId = newSession.sessionId || newSession.session_id;
          const normalizedSession = {
            ...newSession,
            session_id: sessionId,
          };
          console.log(
            "ProposalWorkspace: Created and setting new session:",
            sessionId
          );
          setCurrentChatSession(normalizedSession);
        }
      } catch (error) {
        console.error("Failed to create proposal session:", error);
      }
    }
  };

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      try {
        // Method 1: Try scrollIntoView first (smoothest)
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      } catch (error) {
        // Method 2: Fallback to manual scroll
        const messagesContainer =
          messagesEndRef.current.closest(".overflow-y-auto");
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }
    }
  }, []);

  // Enhanced auto-scroll effect with multiple triggers
  useEffect(() => {
    if (activeTab === "chat") {
      // Immediate scroll for streaming updates
      scrollToBottom();

      // Delayed scroll to handle DOM updates (especially for final message)
      const delayedScrollTimeout = setTimeout(() => {
        scrollToBottom();
      }, 150);

      return () => clearTimeout(delayedScrollTimeout);
    }
  }, [messages, isTyping, streamingContent, activeTab, scrollToBottom]);

  // Additional effect specifically for when streaming stops
  useEffect(() => {
    if (!isStreaming && activeTab === "chat") {
      // When streaming stops, ensure we scroll to show the complete final message
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  }, [isStreaming, activeTab, scrollToBottom]);

  const handleSendMessage = async (message, options = {}) => {
    if (!currentChatSession) {
      console.error("ProposalWorkspace: No currentChatSession available!");
      return;
    }

    const sessionId =
      currentChatSession.session_id || currentChatSession.sessionId;

    if (!sessionId) {
      console.error("ProposalWorkspace: No valid session ID found!");
      return;
    }

    // ADDITIONAL CHECK: Ensure this is a proposal-specific session
    if (currentChatSession.session_type !== "proposal_specific") {
      console.error(
        "ProposalWorkspace: Current session is not proposal-specific! Clearing and creating new session."
      );
      clearCurrentSession();
      setCurrentChatSession(null);
      await createProposalSession();
      return;
    }

    try {
      // Add user message immediately
      const userMessage = {
        message_id: `temp-user-${Date.now()}`,
        message_type: "user",
        message_text: message,
        timestamp: new Date().toISOString(),
        session_id: sessionId,
        referenced_documents: referencedDocuments.map((doc) => doc.document_id),
        referenced_document_details: referencedDocuments,
      };

      setMessages((prev) => [...prev, userMessage]);

      // Add streaming message placeholder
      const streamingMessageId = `streaming-${Date.now()}`;
      const streamingMessage = {
        message_id: streamingMessageId,
        message_type: "assistant",
        message_text: "",
        timestamp: new Date().toISOString(),
        session_id: sessionId,
        isStreaming: true,
      };

      setMessages((prev) => [...prev, streamingMessage]);

      // Clear any previous streaming content
      clearStreamingContent();

      console.log("Starting streaming message...");

      // Send streaming message with selected model
      const completionData = await sendStreamingMessage(sessionId, message, {
        ...options,
        referencedDocuments: referencedDocuments.map((doc) => doc.document_id),
        selectedModel: selectedModel,
      });

      console.log("Streaming completed, updating final message...");

      // Get the final content before any updates
      const finalContent = getFinalStreamedContent() || streamingContent;

      // CRITICAL FIX: Update everything EXCEPT message_id to prevent React from creating new component
      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === streamingMessageId
            ? {
                ...msg,
                // DON'T change message_id - keep the streaming ID to maintain component identity
                message_text: finalContent, // Set the final content immediately
                ai_provider: completionData.provider,
                model_used: completionData.model,
                processing_time_ms: completionData.processingTime,
                source_links: completionData.sources || [],
                isStreaming: false, // Mark as complete immediately
                server_message_id: completionData.messageId, // Store server ID separately if needed
              }
            : msg
        )
      );

      // Now complete the streaming state (this will clear streamingContent)
      completeStreaming();
      refreshSessions(parseInt(proposalId));
    } catch (error) {
      console.error("ProposalWorkspace: Send message failed:", error);

      setMessages((prev) =>
        prev.filter(
          (msg) =>
            !msg.message_id.startsWith("temp-user-") &&
            !msg.message_id.startsWith("streaming-")
        )
      );

      clearStreamingContent();
    }
  };

  const handleNewChat = async (targetProposalId) => {
    if (!currentProposal) return;

    try {
      // FIRST: Clear current session and messages immediately
      setCurrentChatSession(null);
      clearCurrentSession();

      // Create a unique session name
      const timestamp = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const sessionName = `${currentProposal.proposal_name} Chat - ${timestamp}`;

      const newSession = await createSession(sessionName, parseInt(proposalId));

      if (newSession) {
        // FIX: The API returns sessionId (camelCase), but we need session_id (snake_case)
        const sessionId = newSession.sessionId || newSession.session_id;

        if (sessionId) {
          // Create a normalized session object with the correct property name
          const normalizedSession = {
            ...newSession,
            session_id: sessionId,
          };

          // Set as current chat session
          setCurrentChatSession(normalizedSession);

          // Fetch the new session to ensure it's properly loaded
          await fetchSession(sessionId);
        }

        // Switch to chat tab
        setActiveTab("chat");

        // Close chat sidebar on mobile
        setChatSidebarOpen(false);
      }
    } catch (error) {
      console.error("Failed to create new chat session:", error);
    }
  };

  const handleSessionSelect = async (session) => {
    // Get the session ID (handle both camelCase and snake_case)
    const sessionId = session.session_id || session.sessionId;

    console.log(
      "ProposalWorkspace: Selecting session:",
      sessionId,
      "Type:",
      session.session_type
    );

    // CRITICAL CHECK: Only allow proposal-specific sessions in workspace
    if (session.session_type !== "proposal_specific") {
      console.error(
        "ProposalWorkspace: Attempted to select non-proposal session in workspace! Rejecting."
      );
      return;
    }

    // FIRST: Clear current session if switching to a different one
    if (
      currentChatSession?.session_id !== sessionId &&
      currentChatSession?.sessionId !== sessionId
    ) {
      setCurrentChatSession(null);
      clearCurrentSession();

      // Small delay to ensure state is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Normalize the session object to ensure it has session_id
    const normalizedSession = {
      ...session,
      session_id: sessionId,
    };

    // Set the new current chat session
    setCurrentChatSession(normalizedSession);

    // Fetch the session messages
    await fetchSession(sessionId);

    // Make sure we're on the chat tab
    setActiveTab("chat");
  };

  const handleReferenceDocument = (document) => {
    const isAlreadyReferenced = referencedDocuments.some(
      (doc) => doc.document_id === document.document_id
    );

    if (isAlreadyReferenced) {
      setReferencedDocuments((prev) =>
        prev.filter((doc) => doc.document_id !== document.document_id)
      );
    } else {
      setReferencedDocuments((prev) => [...prev, document]);
    }
  };

  const handleRemoveReference = (documentId) => {
    setReferencedDocuments((prev) =>
      prev.filter((doc) => doc.document_id !== documentId)
    );
  };

  const handleDocumentSelect = (document) => {
    navigate(`/workspace/${proposalId}/document/${document.document_id}`);
  };

  const handleBackToDocuments = () => {
    navigate(`/workspace/${proposalId}`);
  };

  const handleDocumentCreated = (newDocument) => {
    setShowCreateDocModal(false);
    fetchDocuments(parseInt(proposalId)); // Refresh documents list
    navigate(`/workspace/${proposalId}/document/${newDocument.document_id}`);
  };

  const handleUploadComplete = () => {
    //setShowUploadModal(false);
    setDocumentRefreshCount((prev) => prev + 1); // Triggers refresh
  };

  const handleDeleteClick = (e, document) => {
    e.stopPropagation(); // Prevent triggering the document select
    setDocumentToDelete(document);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      await deleteDocument(documentToDelete.document_id);
      
      // If we deleted the currently open document, navigate back to document list
      if (currentDocument && currentDocument.document_id === documentToDelete.document_id) {
        navigate(`/workspace/${proposalId}`);
      }
      
      // Refresh the documents list
      fetchDocuments(parseInt(proposalId));
      
      // Close modal and reset state
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      // You could add a toast notification here for better UX
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleDuplicateClick = (e, document) => {
    e.stopPropagation(); // Prevent triggering the document select
    setDocumentToDuplicate(document);
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!documentToDuplicate) return;

    try {
      const result = await apiClient.duplicateDocument(documentToDuplicate.document_id);

      // Refresh the documents list to show the new duplicate
      await fetchDocuments(parseInt(proposalId));

      // Close modal and reset state
      setShowDuplicateModal(false);
      setDocumentToDuplicate(null);

      // Show success message (you could add a toast here)
      console.log('Document duplicated successfully:', result.data.document);
    } catch (error) {
      console.error('Failed to duplicate document:', error);
      // You could add a toast notification here for better UX
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setDocumentToDuplicate(null);
  };

  const handlePredefinedPromptSelect = (prompt) => {
    // Dispatch custom event that MessageInput listens for
    window.dispatchEvent(
      new CustomEvent("predefinedPromptSelected", {
        detail: { prompt },
      })
    );
  };

  if (proposalLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading workspace..." />
        </div>
      </Layout>
    );
  }

  if (!currentProposal) {
    return (
      <Layout title="Proposal Not Found">
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            className="text-4xl text-red-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Proposal Not Found
          </h3>
          <p className="text-slate-600 mb-6">
            The proposal you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`${currentProposal.proposal_name} - Workspace`}>
      <div className="space-y-4">
        {/* Workspace Header - Now Compact */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex items-center space-x-2 text-slate-600"
              >
                <FontAwesomeIcon icon="arrow-left" />
                <span>Dashboard</span>
              </button>

              <div className="h-6 w-px bg-slate-300"></div>

              <div className="flex-1">
                <h1 className="text-xl font-bold text-slate-900">
                  {currentProposal.proposal_name}
                </h1>
                <p className="text-sm text-slate-600">
                  {currentProposal.customer_name &&
                    `${currentProposal.customer_name} • `}
                  {currentProposal.status}
                  {currentProposal.due_date &&
                    ` • Due ${new Date(
                      currentProposal.due_date
                    ).toLocaleDateString()}`}
                </p>
              </div>
            </div>

            {/* Tab Navigation - Moved from bottom to right side */}
            <div className="flex items-center">
              {/* Chat-only workspace - document creation moved to Proposal Development */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
                <div className="flex items-center text-primary-700">
                  <FontAwesomeIcon icon="comment" className="mr-2" />
                  <span className="font-medium text-sm">Chat & Collaborate</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Workspace Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-[calc(100vh-13rem)]">
          <div className="flex h-full">
            {/* Chat Content - Always visible (create tab removed) */}
            <>
                {/* Chat Sidebar */}
                <ChatSidebar
                  isOpen={chatSidebarOpen}
                  onClose={() => setChatSidebarOpen(false)}
                  onNewChat={handleNewChat}
                  currentSessionId={currentChatSession?.session_id}
                  proposalId={parseInt(proposalId)}
                  onSessionSelect={handleSessionSelect}
                />

                {/* Main Chat Area */}
                <div className="flex-1 flex flex-col min-w-0">
                  {/* Chat Header */}
                  <div className="border-b border-slate-200 p-4 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => setChatSidebarOpen(!chatSidebarOpen)}
                          className="lg:hidden p-2 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                          <FontAwesomeIcon
                            icon="bars"
                            className="text-slate-600"
                          />
                        </button>

                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-primary-100 text-primary-600">
                            <FontAwesomeIcon icon="file" />
                          </div>
                          <div>
                            <h2 className="font-semibold text-slate-900">
                              {currentProposal.proposal_name}
                            </h2>
                            <p className="text-sm text-slate-600">
                              AI Assistant with full proposal context
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Model Selection and Predefined Prompts */}
                      <div className="flex items-center space-x-3">
                        {/* AI Model Selector */}
                        <div className="relative">
                          <select
                            value={selectedModel}
                            onChange={(e) => setSelectedModel(e.target.value)}
                            disabled={modelsLoading || chatLoading || isTyping || isStreaming}
                            className="appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-slate-700 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {modelsLoading ? (
                              <option value="">Loading models...</option>
                            ) : (
                              <>
                                <option value="">Select AI Model...</option>
                                <optgroup label="OpenAI">
                                  {availableModels
                                    .filter((m) => m.provider === "openai")
                                    .map((model) => (
                                      <option key={model.model} value={model.model}>
                                        {model.name}
                                      </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Anthropic">
                                  {availableModels
                                    .filter((m) => m.provider === "anthropic")
                                    .map((model) => (
                                      <option key={model.model} value={model.model}>
                                        {model.name}
                                      </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Google">
                                  {availableModels
                                    .filter((m) => m.provider === "google")
                                    .map((model) => (
                                      <option key={model.model} value={model.model}>
                                        {model.name}
                                      </option>
                                    ))}
                                </optgroup>
                              </>
                            )}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                            <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                          </div>
                        </div>

                        {/* Predefined Prompts Dropdown */}
                        <PredefinedPromptsDropdown
                          onSelectPrompt={handlePredefinedPromptSelect}
                          disabled={chatLoading || isTyping || isStreaming}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {chatLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <LoadingSpinner
                          size="lg"
                          text="Loading conversation..."
                        />
                      </div>
                    ) : chatError ? (
                      <div className="flex items-center justify-center h-full">
                        <ErrorMessage message={chatError} />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center max-w-md">
                          <FontAwesomeIcon
                            icon="robot"
                            className="text-6xl text-slate-300 mb-4"
                          />
                          <h3 className="text-xl font-semibold text-slate-900 mb-2">
                            Ready to help with {currentProposal.proposal_name}
                          </h3>
                          <p className="text-slate-600 mb-6">
                            I have access to all documents and context for this
                            proposal. Ask me anything!
                          </p>
                          {/* <div className="space-y-2 text-sm text-slate-500">
                            <p>💡 Try asking:</p>
                            <ul className="space-y-1">
                              <li>"What are the key evaluation criteria?"</li>
                              <li>"Help me write the technical approach"</li>
                              <li>"What past performance is most relevant?"</li>
                            </ul>
                          </div> */}
                        </div>
                      </div>
                    ) : (
                      <>
                        {messages.map((message, index) => {
                          // Handle streaming messages - FIX: Pass streamingContent properly
                          if (message.isStreaming) {
                            return (
                              <MessageBubble
                                key={message.message_id || index}
                                message={{
                                  ...message,
                                  message_text:
                                    streamingContent || "AI is thinking...",
                                }}
                                isLatest={index === messages.length - 1}
                                isStreaming={true}
                                streamingContent={streamingContent} // FIX: Pass streamingContent
                              />
                            );
                          }

                          // Regular messages
                          return (
                            <MessageBubble
                              key={message.message_id || index}
                              message={message}
                              isLatest={index === messages.length - 1}
                              isStreaming={false}
                            />
                          );
                        })}

                        {isTyping && (
                          <div className="flex items-center space-x-3 text-slate-500">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <FontAwesomeIcon
                                icon="robot"
                                className="text-primary-600 text-sm"
                              />
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                              <span className="text-sm">AI is thinking...</span>
                            </div>
                          </div>
                        )}

                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="border-t border-slate-200 p-4 bg-slate-50">
                    <MessageInput
                      onSendMessage={handleSendMessage}
                      disabled={chatLoading || isTyping || isStreaming}
                      placeholder={`Ask about ${currentProposal.proposal_name}...`}
                      referencedDocuments={referencedDocuments}
                      onRemoveReference={handleRemoveReference}
                    />
                  </div>
                </div>

                {/* Document Sidebar - Now with Upload button */}
                <DocumentSidebar
                  isOpen={documentSidebarOpen}
                  onClose={() => setDocumentSidebarOpen(false)}
                  proposalId={parseInt(proposalId)}
                  onReferenceDocument={handleReferenceDocument}
                  referencedDocuments={referencedDocuments}
                  onUpload={() => setShowUploadModal(true)}
                  refreshTrigger={documentRefreshCount} // 👈 NEW
                />
              </>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
        proposalId={parseInt(proposalId)}
      />

      <DuplicateDocumentModal
        isOpen={showDuplicateModal}
        onClose={handleDuplicateCancel}
        document={documentToDuplicate}
        onConfirm={handleDuplicateConfirm}
      />

      {/* Typing Animation Styles */}
      <style jsx>{`
        .typing-indicator {
          display: flex;
          space-x: 2px;
        }

        .typing-indicator span {
          height: 4px;
          width: 4px;
          background-color: #64748b;
          border-radius: 50%;
          display: inline-block;
          animation: typing 1.4s ease-in-out infinite both;
          margin-right: 2px;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes typing {
          0%,
          80%,
          100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Layout>
  );
};

// Document Library View Component
const DocumentLibraryView = ({
  documents,
  onDocumentSelect,
  onDeleteDocument,
  onDuplicateDocument,
  onCreateDocument,
  onHolisticGenerate,
  loading,
  error,
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      Draft: { bg: "bg-slate-100", text: "text-slate-700", icon: "edit" },
      In_Progress: { bg: "bg-blue-100", text: "text-blue-700", icon: "clock" },
      Review: { bg: "bg-amber-100", text: "text-amber-700", icon: "eye" },
      Final: { bg: "bg-emerald-100", text: "text-emerald-700", icon: "check" },
      Archived: { bg: "bg-slate-100", text: "text-slate-500", icon: "archive" },
    };

    const config = statusConfig[status] || statusConfig["Draft"];

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {status}
      </span>
    );
  };

  const getDocumentTypeIcon = (documentType) => {
    const iconMap = {
      Technical_Proposal: "cogs",
      Past_Performance: "history",
      Key_Personnel: "users",
      Pricing_Proposal: "dollar-sign",
      Executive_Summary: "star",
      Corporate_Overview: "building",
      Custom: "file-alt",
    };

    return iconMap[documentType] || "file-alt";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading documents..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <FontAwesomeIcon
          icon="file-alt"
          className="text-4xl text-slate-300 mb-4"
        />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          No Documents Yet
        </h3>
        <p className="text-slate-600 mb-6">
          Create your first proposal document to get started with AI-powered
          authoring.
        </p>
        <div className="flex items-center justify-center space-x-3">
          <button
            onClick={onHolisticGenerate}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <FontAwesomeIcon icon="magic" />
            <span>New Document (Holistic)</span>
          </button>
          <button
            onClick={onCreateDocument}
            className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
          >
            <FontAwesomeIcon icon="plus" />
            <span>New Document (Section Based)</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-900">
          Proposal Documents ({documents.length})
        </h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={onHolisticGenerate}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <FontAwesomeIcon icon="magic" />
            <span>New Document (Holistic)</span>
          </button>
          <button
            onClick={onCreateDocument}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
          >
            <FontAwesomeIcon icon="plus" />
            <span>New Document (Section Based)</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {documents.map((document) => (
          <div
            key={document.document_id}
            className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => onDocumentSelect(document)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon
                  icon={getDocumentTypeIcon(document.document_type)}
                  className="text-primary-500"
                />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {document.document_type.replace("_", " ")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusBadge(document.status)}
                <button
                  onClick={(e) => onDuplicateDocument(e, document)}
                  className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                  title="Duplicate document"
                >
                  <FontAwesomeIcon icon="copy" className="text-sm" />
                </button>
                <button
                  onClick={(e) => onDeleteDocument(e, document)}
                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors duration-200"
                  title="Delete document"
                >
                  <FontAwesomeIcon icon="trash" className="text-sm" />
                </button>
              </div>
            </div>

            <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-primary-600 transition-colors">
              {document.document_name}
            </h3>

            <div className="text-sm text-slate-600 space-y-1">
              <div className="flex items-center justify-between">
                <span>Sections:</span>
                <span className="font-medium">
                  {document.section_count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Words:</span>
                <span className="font-medium">
                  {document.total_word_count || 0}
                </span>
              </div>
              {document.is_ai_generated && (
                <div className="flex items-center space-x-1 text-blue-600">
                  <FontAwesomeIcon icon="robot" className="text-xs" />
                  <span className="text-xs">AI Generated</span>
                </div>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
              Updated {new Date(document.updated_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProposalWorkspace;
